import React from 'react'
import { useIntl } from 'react-intl'
import {
  Chip,
  CircularProgress,
  ClickAwayListener,
  FormControl,
  MenuItem,
  MenuList,
  Paper,
  TextField,
  Typography
} from '@material-ui/core'
import { useKeycloak } from '@react-keycloak/web'
import SubscriptionServices from '../../../../../Services/Consumers/SubscriptionServices'
import { useStyles } from './Styles'

const UserAutocomplete = ({ selectedUser, onUserSelect, onUserRemove, handleNotificationCall }) => {
  const intl = useIntl()
  const classes = useStyles()
  const { keycloak } = useKeycloak()

  const [userSearchText, setUserSearchText] = React.useState('')
  const [isUserSearching, setIsUserSearching] = React.useState(false)
  const [userSearchResults, setUserSearchResults] = React.useState([])
  const [showUserDropdown, setShowUserDropdown] = React.useState(false)
  const debounceTimer = React.useRef(null)

  // Cleanup debounce timer on unmount
  React.useEffect(() => {
    const timer = debounceTimer.current

    return () => {
      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [])

  // User search handler
  const handleUserSearch = async (searchTerm) => {
    if (searchTerm.length < 2) {
      setUserSearchResults([])
      setShowUserDropdown(false)
      return
    }
    setIsUserSearching(true)
    SubscriptionServices.searchUsers({
      headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` },
      params: { name: searchTerm }
    }).then((result) => {
      if (!!result && !!result?.data && result?.data?.status?.code === 200) {
        setUserSearchResults(result.data.data)
        setShowUserDropdown(true)
      } else {
        let message = intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' })
        if (result.response && result.response.data) {
          if (result.response.data && result.response.data.status && result.response.data.status.errors) {
            message = result.response.data.status.errors
          }
        }
        handleNotificationCall({
          type: 'defence',
          message: message,
          color: 'error'
        }, 'error')
        setUserSearchResults([])
        setShowUserDropdown(false)
      }
    }).then(() => {
      setIsUserSearching(false)
    })
  }

  // Debounced search change handler
  const handleUserSearchChange = (e) => {
    const value = e.target.value
    setUserSearchText(value)
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }
    debounceTimer.current = setTimeout(() => {
      handleUserSearch(value)
    }, 500)
  }

  // Handle user selection
  const handleSelectUser = (user) => {
    onUserSelect(user)
    setUserSearchText('')
    setUserSearchResults([])
    setShowUserDropdown(false)
  }

  return (
    <FormControl variant="outlined">
      <ClickAwayListener onClickAway={() => setShowUserDropdown(false)}>
        <div className={classes.autocompleteWrapper}>
          <TextField
            placeholder={intl.formatMessage({ id: 'Fields.input.PromocodeRuleUser.placeholder' })}
            id="user_id"
            type={'text'}
            margin="dense"
            variant="outlined"
            value={userSearchText}
            onChange={handleUserSearchChange}
            onFocus={() => {
              if (userSearchResults.length > 0) {
                setShowUserDropdown(true)
              }
            }}
            disabled={!!Object.keys(selectedUser).length}
            InputProps={{
              endAdornment: isUserSearching && <CircularProgress size={20} />
            }}
          />

          {/* Dropdown Results */}
          {showUserDropdown && !Object.keys(selectedUser).length && (
            <Paper className={classes.dropdown}>
              {
                userSearchResults.length > 0 ?
                  <MenuList>
                    {userSearchResults.map((user) => (
                      <MenuItem
                        key={user.id}
                        onClick={() => handleSelectUser(user)}
                        className={classes.menuItem}
                      >
                        <div>
                          <div className={classes.userName}>
                            {user.first_name} {user.last_name}
                          </div>
                          <div className={classes.userEmail}>
                            {user.email}
                          </div>
                        </div>
                      </MenuItem>
                    ))}
                  </MenuList>
                  :
                  <Typography variant="body1" className={classes.usersNoResultsText}>
                    {intl.formatMessage({ id: 'UserGroups.List.Users.NoResults' })}
                  </Typography>
              }
            </Paper>
          )}

          {/* Selected User */}
          {!!Object.keys(selectedUser).length && (
            <div style={{ marginTop: '8px' }}>
              <Chip
                label={`${selectedUser.first_name} ${selectedUser.last_name}${selectedUser.email ? ` (${selectedUser.email})` : ''}`}
                onDelete={onUserRemove}
                className={classes.userChip}
                color="primary"
                variant="outlined"
              />
            </div>
          )}
        </div>
      </ClickAwayListener>
    </FormControl>
  )
}

export default UserAutocomplete

